import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import GsplatViewer from '../components/GsplatViewer'
import ModelViewer from '../components/ModelViewer'
import { getScan, type Scan } from '../lib/api'

export default function Viewer() {
  const { id } = useParams<{ id: string }>()
  const [scan, setScan] = useState<Scan | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    let pollHandle: ReturnType<typeof setTimeout> | null = null

    async function tick() {
      try {
        const s = await getScan(id!)
        if (cancelled) return
        setScan(s)
        if (s.status === 'pending' || s.status === 'processing') {
          pollHandle = setTimeout(tick, 2500)
        }
      } catch (e: any) {
        if (cancelled) return
        setError(e?.message ?? 'Scan no encontrado')
      }
    }
    tick()
    return () => {
      cancelled = true
      if (pollHandle) clearTimeout(pollHandle)
    }
  }, [id])

  if (error) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-red-400">{error}</p>
        <Link to="/" className="text-sm underline text-[var(--color-muted)]">go home</Link>
      </main>
    )
  }

  if (!scan || scan.status === 'pending' || scan.status === 'processing') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="w-32 h-32 rounded-full border-2 border-[var(--color-accent)] glow-pulse" />
        <p className="text-sm text-[var(--color-muted)] font-mono">
          {scan?.status ?? 'queued'}... reconstructing
        </p>
      </main>
    )
  }

  if (scan.status === 'failed') {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-red-400">reconstruction failed</p>
        <p className="text-xs text-[var(--color-muted)]">{scan.error ?? 'unknown error'}</p>
        <Link to="/" className="text-sm underline text-[var(--color-muted)]">try another</Link>
      </main>
    )
  }

  return (
    <main className="min-h-screen flex flex-col">
      <header className="absolute top-0 left-0 right-0 z-10 px-6 py-4 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent">
        <Link to="/" className="font-mono text-sm">eigen-splat</Link>
        <div className="flex gap-3 text-xs">
          {(scan.meshUrl || scan.splatUrl) && (
            <a
              href={scan.meshUrl ?? scan.splatUrl}
              download
              className="px-3 py-1.5 border border-white/20 rounded hover:bg-white/10 transition"
            >
              download {scan.meshUrl ? '.glb' : '.ply'}
            </a>
          )}
          <button
            onClick={() => navigator.clipboard.writeText(window.location.href)}
            className="px-3 py-1.5 bg-[var(--color-accent)] rounded hover:opacity-90 transition"
          >
            copy link
          </button>
        </div>
      </header>
      <div className="flex-1 w-full">
        {scan.meshUrl ? (
          <ModelViewer url={scan.meshUrl} />
        ) : scan.splatUrl ? (
          <GsplatViewer url={scan.splatUrl} />
        ) : (
          <div className="flex items-center justify-center h-full text-[var(--color-muted)]">
            no scan URL returned
          </div>
        )}
      </div>
    </main>
  )
}
